import React from "react";
import ShowWithSkeleton from "src/utils/show-with-skeleton";
import executiveValueFormatter from "src/scenes/overview/overview-components/executive-value-formatter";
import styled from "styled-components";
import { Box, Paper, Stack } from "@mui/material";
import ReactCardFlip from "react-card-flip";
import calculateDependencies from "src/scenes/overview/overview-components/calculate-dependencies";
import AccuracyLinear from "src/scenes/overview/overview-components/accuracy-linear";
import { docsPathConstants, getDocsPath } from "../../../../utils/docs-path-constants";
import ExternalLink from "../../../../components/external-link";

const CustomPaper = styled(Paper)`
    width: 100%;
    padding: 16px;
    border-radius: 0 0 14px 14px;
`;

const MetricHeaderText = styled.p`
    margin: 12px 0 12px 0;
    font-size: 15px;
    font-weight: 600;
`;

const MetricBreakdownText = styled.p`
    margin: 0;
    font-size: 12px;
    font-weight: 300;
`;

const InfoText = styled.p`
    margin: 0;
    font-size: 12px;
    font-weight: 200;
`;

const BreakdownContainer = styled(Box)`
    padding: 12px;
    border-radius: 12px;
    background: #393939;
    margin-top: 12px;
`;

const DeliverySectionOutputEfficacy = ({ data, fetchAction, expandedView, isFlipped }) => {
    return (
        <ReactCardFlip isFlipped={isFlipped} containerStyle={{ width: "100%", height: "100%" }}>
            <CustomPaper>
                {!isFlipped && (
                    <>
                        <MetricHeaderText style={{ margin: 0, fontWeight: 200, opacity: 0.5 }}>
                            Process efficacy
                        </MetricHeaderText>
                        <Stack direction="column" mb={2}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <MetricHeaderText>Sprint Planning Accuracy</MetricHeaderText>
                                <AccuracyLinear
                                    data={
                                        data &&
                                        data.scopeCreep &&
                                        data.scopeCreep.meta &&
                                        data.scopeCreep.meta.accuracy
                                    }
                                />
                            </Stack>
                            <ShowWithSkeleton
                                fetchAction={fetchAction}
                                height={50}
                                width="100%"
                                streamingProperty={
                                    fetchAction && fetchAction.data && fetchAction.data.scopeCreep
                                }
                            >
                                {executiveValueFormatter(
                                    data &&
                                        data.scopeCreep &&
                                        data.scopeCreep.total &&
                                        data.scopeCreep.total.value,
                                    "custom",
                                    false,
                                    "issues scope creep",
                                    data &&
                                        data.scopeCreep &&
                                        data.scopeCreep.stats &&
                                        data.scopeCreep.stats.slope
                                )}
                            </ShowWithSkeleton>
                            {expandedView && (
                                <BreakdownContainer>
                                    <MetricBreakdownText style={{ textAlign: "center" }}>
                                        Contributed To
                                    </MetricBreakdownText>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>Sprints: </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.scopeCreep &&
                                                fetchAction.data.scopeCreep.breakdown
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.scopeCreep &&
                                                    data.scopeCreep.breakdown.sprintsCount,
                                                null,
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            Planned sprint issues:
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.scopeCreep &&
                                                fetchAction.data.scopeCreep.breakdown
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.scopeCreep &&
                                                    data.scopeCreep.breakdown.planned,
                                                null,
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            Scope creeped sprint issues:{" "}
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.scopeCreep
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.scopeCreep &&
                                                    data.scopeCreep.total.value,
                                                null,
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            Total sprint issues:{" "}
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.scopeCreep &&
                                                fetchAction.data.scopeCreep.breakdown
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.scopeCreep &&
                                                    data.scopeCreep.breakdown.total,
                                                null,
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                </BreakdownContainer>
                            )}
                        </Stack>
                        <Stack direction="column" mb={2}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <MetricHeaderText>Reviewing Efficiency</MetricHeaderText>
                                <AccuracyLinear
                                    data={
                                        data &&
                                        data.reviewTime &&
                                        data.reviewTime.meta &&
                                        data.reviewTime.meta.accuracy
                                    }
                                />
                            </Stack>
                            <ShowWithSkeleton
                                fetchAction={fetchAction}
                                height={50}
                                width="100%"
                                streamingProperty={
                                    fetchAction && fetchAction.data && fetchAction.data.reviewTime
                                }
                            >
                                {executiveValueFormatter(
                                    data &&
                                        data.reviewTime &&
                                        data.reviewTime.breakdown &&
                                        data.reviewTime.breakdown.reviewToMergeTimeGitAdj,
                                    "time",
                                    false,
                                    <>
                                        <br />
                                        (PRs Merged - no outliers)
                                    </>,
                                    data &&
                                        data.reviewTime &&
                                        data.reviewTime.stats &&
                                        data.reviewTime.stats.slope
                                )}
                            </ShowWithSkeleton>
                            {expandedView && (
                                <BreakdownContainer>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            PR Review Time Mean (+outliers):
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.reviewTime &&
                                                fetchAction.data.reviewTime.breakdown
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.reviewTime &&
                                                    data.reviewTime.breakdown.reviewToMergeTimeGit,
                                                "time",
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            PR Review Time median:
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.reviewTime &&
                                                fetchAction.data.reviewTime.breakdown
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.reviewTime &&
                                                    data.reviewTime.breakdown
                                                        .reviewToMergeTimeGitMedian,
                                                "time",
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            PR Review Time 95th percentile:
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.reviewTime &&
                                                fetchAction.data.reviewTime.breakdown
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.reviewTime &&
                                                    data.reviewTime.breakdown
                                                        .reviewToMergeTimeGit95th,
                                                "time",
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                </BreakdownContainer>
                            )}
                        </Stack>
                    </>
                )}
            </CustomPaper>
            <CustomPaper>
                {isFlipped && (
                    <>
                        <MetricHeaderText
                            style={{ margin: "0 0 10px 0", fontWeight: 200, opacity: 0.5 }}
                        >
                            Process efficacy
                        </MetricHeaderText>
                        <InfoText>
                            How efficient were some of the processes used to deliver the output.
                        </InfoText>
                        <Stack direction="column" mb={2}>
                            <MetricHeaderText>
                                <ExternalLink
                                    href={getDocsPath(
                                        docsPathConstants.deliverySprintPlanningDefinition
                                    )}
                                    underline="none"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Sprint Planning Accuracy
                                </ExternalLink>
                            </MetricHeaderText>
                            <InfoText>
                                Volume (number of issues) of scope creep in the Jira sprints that
                                this group of people contributed to (only tickets that we created by
                                or assigned to this group are measured)
                            </InfoText>
                            <Stack
                                direction="column"
                                alignItems="left"
                                spacing={1}
                                mt={1}
                                style={{ width: "100%" }}
                            >
                                <InfoText>Data source impact:</InfoText>
                                {calculateDependencies(
                                    data &&
                                        data.scopeCreep &&
                                        data.scopeCreep.meta.accuracy.dependencies
                                )}
                            </Stack>
                        </Stack>
                        <Stack direction="column" mb={2}>
                            <MetricHeaderText>
                                <ExternalLink
                                    href={getDocsPath(
                                        docsPathConstants.deliveryReviewingEfficiencyDefinition
                                    )}
                                    underline="none"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Reviewing Efficiency
                                </ExternalLink>
                            </MetricHeaderText>
                            <InfoText>
                                Time it took for PRs from the beginning if their review to their
                                merge.
                            </InfoText>
                            <Stack
                                direction="column"
                                alignItems="left"
                                spacing={1}
                                mt={1}
                                style={{ width: "100%" }}
                            >
                                <InfoText>Data source impact:</InfoText>
                                {calculateDependencies(
                                    data &&
                                        data.reviewTime &&
                                        data.reviewTime.meta.accuracy.dependencies
                                )}
                            </Stack>
                        </Stack>
                    </>
                )}
            </CustomPaper>
        </ReactCardFlip>
    );
};

export default DeliverySectionOutputEfficacy;
